<template>
  <component
    :is="isType"
    :href="url"
    :type="buttonIs"
    :class="['button', cssClasses]"
    :disabled="disabled"
    @click="$emit('click', $event)"
    @mouseover="$emit('mouseover', $event)"
    @mouseleave="$emit('mouseleave', $event)">
    <slot name="icon"></slot><span>{{ text }}</span>
  </component>
</template>

<script>
export default {
  props: {
    isType: {
      type: String,
      default: 'button'
    },
    url: {
      type: String,
      default: undefined
    },
    buttonType: {
      type: String,
      default: 'button'
    },
    text: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    componentIs() {
      return this.isType !== 'a' ? 'button' : 'a'
    },
    buttonIs() {
      return this.isType === 'a' ? undefined : this.buttonType
    }
  }
}
</script>

<style lang="scss">
.button {
  @include body-semi-bold;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 3.125rem;
  padding: 0 measure(half);
  border: none;
  text-decoration: none;
  transition: all 140ms ease-in;

  &:hover {
    cursor: pointer;
    transition: all 140ms ease-out;
  }
}

.button--gray {
  @include body-smaller-semi-bold;
  background-color: #eeeeee;
  color: color(text);

  &:hover {
    background-color: color(gray-4);
  }
}

.button--white {
  background-color: color(white);
  border: 1px solid color(gray-4);

  &:hover {
    border: 1px solid color(gray-2);
  }
}

.button--icon-left svg {
  order: 0;
  margin-right: measure(quarter);
}
.button--icon-right svg {
  margin-left: measure(quarter);
}

.button--icon-only {
  height: auto;
  padding: 0;
  overflow: hidden;
  background-color: transparent;

  > svg {
    pointer-events: none;
  }
}

.button--with-border {
  color: color(text-inverted);
  border-radius: 0;
  padding-bottom: 0.4375rem;
}

.button--green {
  background-color: color(button-action-green);
  box-shadow: inset 0 -0.4375rem color(button-action-border-green);

  &:hover {
    background-color: color(button-action-border-green);
    box-shadow: inset 0 -0.4375rem color(button-action-green);
  }
}

.button--blue {
  background-color: color(button-action-blue);
  color: color(text-inverted);
  box-shadow: inset 0 -0.4375rem color(button-action-border-blue);

  &:hover {
    background-color: color(button-action-border-blue);
    box-shadow: inset 0 -0.4375rem color(button-action-blue);
  }

}

.button--red {
  background-color: color(button-action-red);
  color: color(text-inverted);
  box-shadow: inset 0 -0.4375rem color(button-action-border-red);

  &:hover {
    background-color: color(button-action-border-red);
    box-shadow: inset 0 -0.4375rem color(button-action-red);
  }
}

.button--dark-red {
  background-color: #9E0200;
  color: color(text-inverted);
  box-shadow: inset 0 -0.4375rem #6F0200;

  &:hover {
    background-color: #6F0200;
    box-shadow: inset 0 -0.4375rem #9E0200;
  }
}

.button--transparent {
  @include caption-semi-bold;
  background-color: #2c4d7f;
  color: color(text-inverted);
}

.button--light-blue {
  @include body;
  background-color: color(button-background-light-blue);
  box-shadow: inset 0 -0.4375rem color(button-border-blue);
  color: color(text);

  &:hover {
    background-color: color(button-border-blue);
    box-shadow: inset 0 -0.4375rem color(button-background-light-blue);
  }
}

.button--ghost {
  @include body-smaller-semi-bold;
  width: 9.375rem;
  height: 2.5rem;
  background-color: transparent;
  border: 1px solid color(border-blue-2);
  border-radius: other(border-radius);
  color: color(text-inverted);
  padding-right: measure(quarter);
  padding-left: measure(quarter);

  &:hover {
    background-color: color(border-blue-2);
    cursor: pointer;
  }
}

.button--ghost-is-active {
  background-color: color(border-blue-2);
}

.button--ghost-modal {
  color: color(text-brand-blue);

  &:hover {
    color: color(text-inverted);
  }
}

.button--ghost-green {
  background-color: color(button-action-green);

  &:hover {
    background-color: color(button-action-border-green);
  }
}

.button[disabled] {
  pointer-events: none;
  opacity: 0.5;
}

.button--no-background {
  background-color: transparent;
  color: color(text);

  &:hover {
    text-decoration: underline;
  }
}

.button-link {
  justify-content: center;
  background-color: color(button-background-blue-dark);
  color: color(button-text);
  text-decoration: none;
  height: 3.5rem;
  border-radius: other(border-radius);
  border: none;
  text-decoration: none;

  &:hover {
    cursor: pointer;
    background-color: color(button-action-active);
  }
}

.button-link--ghost {
  background-color: transparent;
  color: color(text-brand-blue);
  border: 1px solid color(button-action-active);

  &:hover {
    color: color(text-inverted);
  }
}

.button-link--active {
  background-color: color(button-action-active);
}

.button-link--narrow {
  height: 2.5rem;
}

.button--true-ghost {
  background-color: transparent;
  color: color(text);

  &:hover {
    text-decoration: underline;
  }
}

.button--blue-no-border {
  height: 3.125rem;
}

/** icon-button start */

.icon-button {
  @include reset-button;
  display: inline-flex;
}

/** icon-button end */
</style>
